<script setup lang="ts">
	import _ from 'lodash'
	import { computed, ref } from 'vue'

	import BreadCrumb from '@/components/BreadCrumb.vue'
	import HasPermission from '@/components/HasPermission.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import StyledInput from '@/components/StyledInput.vue'

	import useInternalRoles from '../../roles/composables/useInternalRoles'
	import UserFiltersInternal from '../components/UserFiltersInternal.vue'
	import UsersListInternal from '../components/UsersListInternal.vue'
	import useAccountInternal from '../composables/useInternalAccount'
	import useInternalUsers from '../composables/useInternalUsers'

	const pages = [
		{ name: 'Usuarios', to: { name: 'users.index' }, current: true },
	]

	const openSliderFilter = ref(false)

	const { data, isLoading, refetch, page, setPage, filters } = useInternalUsers(
		{
			immediate: true,
		}
	)

	// Computed para manejar la paginación de forma segura
	const safePagination = computed(() => {
		return (
			_.omit(data.value, ['data']) || {
				links: [],
				meta: {
					currentPage: 0,
					from: 0,
					lastPage: 0,
					links: [],
					path: '',
					perPage: 0,
					to: 0,
					total: 0,
				},
			}
		)
	})

	const handlePage = (newPage: string) => {
		const pageNumber = Number(newPage)

		if (pageNumber >= 1 && pageNumber <= data.value?.meta.lastPage!) {
			setPage(newPage)
			refetch()
		}
	}

	const toggleSliderFilter = () => {
		openSliderFilter.value = !openSliderFilter.value
	}

	const handleStatusUpdate = (value: string) => {
		filters.status = value
		setPage('1')
		refetch()
	}

	const handleRoleUpdate = (value: string | number) => {
		filters.role = value
		setPage('1')
		refetch()
	}

	const handleSortByUpdate = (value: string) => {
		filters.sortBy = value
		setPage('1')
		refetch()
	}

	const handleSortUpdate = (value: string) => {
		filters.sort = value
		setPage('1')
		refetch()
	}

	const handleResetFilters = () => {
		filters.status = 'all'
		filters.role = 'all'
		filters.sortBy = 'all'
		filters.sort = 'asc'
		setPage('1')
		refetch()
	}

	const { roles } = useInternalRoles({
		immediate: true,
		errorHandling: false,
	})

	const { deactivateAccountMutation, reactivateAccountMutation } =
		useAccountInternal()

	const handleDeactivateAccount = async (id: string) => {
		await deactivateAccountMutation.mutateAsync({
			params: {
				id: Number(id),
			},
			config: {
				raiseToast: true,
				onSuccessToast: 'Cuenta desactivada correctamente',
			},
		})

		refetch()
	}

	const handleReactivateAccount = async (id: string) => {
		await reactivateAccountMutation.mutateAsync({
			params: {
				id: Number(id),
			},
			config: {
				raiseToast: true,
				onSuccessToast: 'Cuenta reactivada correctamente',
			},
		})

		refetch()
	}
</script>

<template>
	<BreadCrumb :pages="pages" />

	<main
		class="flex flex-col mt-2 sm:mt-4 px-3 sm:px-6 lg:px-8 w-full max-w-7xl mx-auto"
	>
		<div class="flex flex-col w-full gap-3 sm:gap-4">
			<!-- Header Section -->
			<div
				class="bg-white rounded-lg sm:rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6"
			>
				<div
					class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 sm:gap-4 mb-4 sm:mb-6"
				>
					<div class="min-w-0 flex-1">
						<h1
							class="font-bold text-xl sm:text-2xl lg:text-3xl text-gray-900 truncate"
						>
							Usuarios Internos
						</h1>
						<p class="text-gray-600 mt-1 text-sm sm:text-base truncate">
							Administra y supervisa todos los usuarios internos del sistema
						</p>
					</div>

					<!-- Controles de búsqueda y acciones en una sola fila -->
					<div class="w-full lg:w-auto">
						<div class="flex gap-2 sm:gap-3 items-center">
							<StyledInput
								v-model="filters.search"
								placeholder="Buscar usuarios..."
								class="flex-1 sm:w-64 lg:w-80"
								input-class="border border-gray-300 w-full h-[36px] rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
							/>
							<!-- Botón de filtros -->
							<button
								@click="toggleSliderFilter"
								class="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 border border-gray-300 min-w-0 flex-shrink-0"
							>
								<svg
									class="w-4 h-4 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
									></path>
								</svg>
								<span class="hidden sm:inline text-xs">Filtros</span>
							</button>

							<!-- Botón Nuevo usuario -->
							<HasPermission
								:entity-type="'App\\Models\\User'"
								:name-permission="'create-internal'"
							>
								<RouterLink
									:to="{ name: 'users.create.internal' }"
									class="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md min-w-0 flex-shrink-0"
								>
									<svg
										class="w-4 h-4 flex-shrink-0"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 6v6m0 0v6m0-6h6m-6 0H6"
										></path>
									</svg>
									<span class="text-xs">Nuevo</span>
									<span class="hidden sm:inline text-xs">usuario</span>
								</RouterLink>
							</HasPermission>
						</div>
					</div>
				</div>
			</div>

			<template v-if="data?.data">
				<!-- Contenedor de tabla optimizado para móviles -->
				<div
					class="relative bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden"
				>
					<!-- Lista de usuarios -->
					<UsersListInternal
						:users="data?.data || []"
						:page="page"
						:pagination="safePagination"
						@update:changePage="handlePage"
						@refetch="refetch"
						:is-tab="false"
						:companyName="'Internos'"
						@deactivate-user="handleDeactivateAccount"
						@reactivate-user="handleReactivateAccount"
						:buttonsList="[
							{
								iconName: 'add',
								name: 'Agregar usuario',
								href: 'users.create.internal',
							},
						]"
						:to-show-user="{
							name: 'users.show.internal',
						}"
						:to-edit-user="{
							name: 'users.edit.internal',
						}"
						:to-permissions-user="{
							name: 'users.permissions.internal',
						}"
					/>
				</div>
			</template>
		</div>
	</main>

	<!-- UserFiltersInternal Component with SlideOver -->
	<UserFiltersInternal
		:open="openSliderFilter"
		:filters="filters"
		:roles="roles || []"
		@close="toggleSliderFilter"
		@update:status="handleStatusUpdate"
		@update:role="handleRoleUpdate"
		@update:sortBy="handleSortByUpdate"
		@update:sort="handleSortUpdate"
		@reset="handleResetFilters"
	/>

	<LoadingModal :when="isLoading" />
</template>

<style scoped>
	.list-enter-active,
	.list-leave-active {
		transition: all 1s ease;
	}

	.list-enter-from,
	.list-leave-to {
		opacity: 0;
		transform: translateX(30px);
	}
</style>
